import { useMemo } from "react";
import { BarChart, Card } from "@tremor/react";

import { type ResponseTimeDistribution } from "../hooks/useChatResponseMetrics";

interface ResponseTimeDistributionChartProps {
  data: ResponseTimeDistribution[];
  avgBusinessMinutes: number | null;
}

const formatMinutes = (minutes: number | null): string => {
  if (minutes === null) return "-";
  if (minutes < 1) return `${Math.round(minutes * 60)} sek`;
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  if (remainingMinutes === 0) return `${hours}h`;
  return `${hours}h ${remainingMinutes}min`;
};

export const ResponseTimeDistributionChart = ({ data, avgBusinessMinutes }: ResponseTimeDistributionChartProps) => {
  const chartData = useMemo(() => {
    return data.map((item) => ({
      name: item.label,
      "Liczba odpowiedzi": item.count,
    }));
  }, [data]);

  const totalResponses = useMemo(() => {
    return data.reduce((sum, item) => sum + item.count, 0);
  }, [data]);

  const hasData = data.some((item) => item.count > 0);

  return (
    <Card>
      <h3 className="text-tremor-content-strong dark:text-dark-tremor-content-strong font-medium">
        Rozkład czasu pierwszej odpowiedzi
      </h3>

      <div className="mt-2 flex gap-6">
        <div>
          <p className="text-tremor-content dark:text-dark-tremor-content text-sm">Średni czas</p>
          <p className="text-tremor-content-strong dark:text-dark-tremor-content-strong text-2xl font-semibold">
            {formatMinutes(avgBusinessMinutes)}
          </p>
        </div>
        <div>
          <p className="text-tremor-content dark:text-dark-tremor-content text-sm">Łącznie odpowiedzi</p>
          <p className="text-tremor-content-strong dark:text-dark-tremor-content-strong text-2xl font-semibold">
            {totalResponses}
          </p>
        </div>
      </div>

      {!hasData ? (
        <p className="text-tremor-content dark:text-dark-tremor-content mt-4">Brak danych do wyświetlenia</p>
      ) : (
        <BarChart
          className="mt-4 h-52"
          data={chartData}
          index="name"
          categories={["Liczba odpowiedzi"]}
          colors={["blue"]}
          showLegend={false}
          showYAxis={true}
          showGridLines={true}
          allowDecimals={false}
        />
      )}
    </Card>
  );
};
